/**
 * Conveyor - Belt System for Package Transport
 * 
 * Manages the conveyor belt physics using SPATIAL DETECTION:
 * - Any package within the belt bounds automatically gets belt physics applied
 * - Packages that land on the belt are automatically picked up
 * - No explicit add/remove tracking needed
 * 
 * Rotation Stabilization:
 * - Uses torque-based correction instead of forced rotation resets
 * - Applies high angular damping to resist rotation
 * - Allows packages to wobble then settle naturally
 * - Packages can still be pushed/tipped by other packages
 */

import { PhysicsWorld, PhysicsBody } from '../engine/PhysicsWorld';
import { CONVEYOR_PHYSICS } from './Constants';

/** Conveyor geometry for rendering and physics */
export interface ConveyorGeometry {
    /** Left edge X position (can be negative for off-screen) */
    x: number;
    /** Top Y position of the belt surface */
    y: number;
    /** Total width of the belt */
    width: number;
    /** Belt thickness (height) */
    height: number;
    /** Right edge where packages fall off */
    endX: number;
    /** X position where new packages spawn (off-screen) */
    spawnX: number;
}

/** Configuration for conveyor behavior */
export interface ConveyorConfig {
    /** Base belt speed in pixels/second */
    baseSpeed: number;
    /** Speed multiplier from difficulty */
    speedMultiplier: number;
    /** Y velocity threshold to consider package "resting" on belt */
    restingThreshold: number;
    /** How far above belt surface to check for packages (pixels) */
    detectionHeight: number;
}

const DEFAULT_CONFIG: ConveyorConfig = {
    baseSpeed: 80,
    speedMultiplier: 1.0,
    restingThreshold: 50,         // px/s - package is considered resting if Y velocity < this
    detectionHeight: 80,          // Check for packages up to 80px above belt surface
};

/** Stabilization state for a package on the belt */
interface BeltStabilization {
    /** Target angle to maintain (captured when entering belt) */
    targetAngle: number;
}

/**
 * Conveyor class - manages belt physics via spatial detection
 */
export class Conveyor {
    private physics: PhysicsWorld;
    private config: ConveyorConfig;
    private geometry: ConveyorGeometry;
    
    // Belt collider handle (static body)
    private beltBodyHandle: number | null = null;
    
    // Animation offset for rendering
    private animationOffset = 0;
    
    // Stabilization tracking for packages on belt (handle -> stabilization state)
    private stabilization: Map<number, BeltStabilization> = new Map();

    constructor(physics: PhysicsWorld, config?: Partial<ConveyorConfig>) {
        this.physics = physics;
        this.config = { ...DEFAULT_CONFIG, ...config };
        
        // Initialize with dummy geometry
        this.geometry = {
            x: 0,
            y: 0,
            width: 0,
            height: 20, // Will be overwritten by setLayout
            endX: 0,
            spawnX: 0,
        };
    }

    /**
     * Set up conveyor geometry from layout dimensions.
     * Layout provides all the pre-calculated responsive values.
     */
    setLayoutFromDimensions(
        conveyorX: number,
        conveyorY: number,
        conveyorWidth: number,
        conveyorHeight: number,
        conveyorEndX: number,
        conveyorSpawnX: number,
        detectionHeight: number,
        restingThreshold: number
    ): void {
        this.geometry = {
            x: conveyorX,
            y: conveyorY,
            width: conveyorWidth,
            height: conveyorHeight,
            endX: conveyorEndX,
            spawnX: conveyorSpawnX,
        };
        
        // Update config with scaled values
        this.config.detectionHeight = detectionHeight;
        this.config.restingThreshold = restingThreshold;

        // Create static belt collider
        this.createBeltBody();
    }

    /**
     * Set up conveyor geometry based on screen dimensions.
     * DEPRECATED: Use setLayoutFromDimensions instead for responsive scaling.
     * The conveyor extends from OFF-SCREEN LEFT to 30% into the container.
     * Packages are pushed continuously until they fall off the edge.
     */
    setLayout(_screenWidth: number, screenHeight: number, containerLeft: number, containerRight: number): void {
        // Fallback for backwards compatibility - use approximate values
        const height = Math.max(screenHeight * 0.025, 16);
        const y = screenHeight * 0.25;
        
        // Start off-screen left (negative X)
        const offScreenPadding = screenHeight * 0.15;
        const x = -offScreenPadding;
        
        // Calculate drop zone: 30% into the container from the left edge
        const containerWidth = containerRight - containerLeft;
        const dropZone = containerLeft + containerWidth * 0.30;
        
        // Belt ends EXACTLY at the drop zone - no overhang
        // Packages will be pushed until they fall off
        const width = dropZone - x;
        
        // endX is the right edge of the belt - packages fall off here
        const endX = dropZone;
        
        // Spawn position is off-screen left
        const spawnX = -offScreenPadding + (offScreenPadding * 0.33);
        
        this.geometry = {
            x,
            y,
            width,
            height,
            endX,
            spawnX,
        };
        
        // Update config with scaled detection values
        this.config.detectionHeight = screenHeight * 0.10;
        this.config.restingThreshold = screenHeight * 0.06;

        // Create static belt collider
        this.createBeltBody();
    }

    /**
     * Create the static belt body for packages to rest on.
     */
    private createBeltBody(): void {
        // Remove existing belt if any
        if (this.beltBodyHandle !== null) {
            this.physics.removeStaticBody(this.beltBodyHandle);
        }

        // Use createStaticBody - this creates a body that isn't tracked
        // in the bodies map, so it won't be grabbable or rendered as a package
        this.beltBodyHandle = this.physics.createStaticBody({
            x: this.geometry.x + this.geometry.width / 2,
            y: this.geometry.y + this.geometry.height / 2,
            shape: {
                type: 'box',
                width: this.geometry.width,
                height: this.geometry.height,
            },
            friction: 1.0,
            restitution: 0,
        });
    }

    /**
     * Get the effective belt speed (base * multiplier).
     */
    getBeltSpeed(): number {
        return this.config.baseSpeed * this.config.speedMultiplier;
    }

    /**
     * Set the speed multiplier (from difficulty settings).
     */
    setSpeedMultiplier(multiplier: number): void {
        this.config.speedMultiplier = multiplier;
    }

    /**
     * Get conveyor geometry for rendering.
     */
    getGeometry(): ConveyorGeometry {
        return this.geometry;
    }

    /**
     * Get spawn position for new packages.
     * Returns position just above the belt, off-screen left.
     */
    getSpawnPosition(packageHeight: number): { x: number; y: number } {
        return {
            x: this.geometry.spawnX,
            y: this.geometry.y - packageHeight / 2 - 5,
        };
    }

    /**
     * Get current animation offset for belt pattern rendering.
     */
    getAnimationOffset(): number {
        return this.animationOffset;
    }

    /**
     * Check if a body is within the conveyor detection zone.
     * Returns true if the body is:
     * - Within the belt's X bounds (package center on belt)
     * - Close to the belt surface (within detectionHeight)
     * - Has low Y velocity (is resting, not falling fast)
     * 
     * Packages are pushed until their CENTER passes the belt edge,
     * at which point gravity tips them off.
     */
    private isBodyOnBelt(body: PhysicsBody): boolean {
        const pos = this.physics.getPosition(body.handle);
        const vel = this.physics.getVelocity(body.handle);
        
        if (!pos || !vel) return false;
        
        // Check X bounds - push until center is past the belt edge
        // Once center passes endX, gravity takes over and tips them off
        if (pos.x < this.geometry.x || pos.x > this.geometry.endX) {
            return false;
        }
        
        // Check Y position (must be near belt surface, above it)
        const beltTop = this.geometry.y;
        const maxHeight = beltTop - this.config.detectionHeight;
        
        // Package center should be above belt surface but not too high
        if (pos.y > beltTop || pos.y < maxHeight) {
            return false;
        }
        
        // Check if package is relatively stable (low Y velocity)
        // This prevents grabbing packages that are falling fast through the zone
        if (Math.abs(vel.y) > this.config.restingThreshold) {
            return false;
        }
        
        return true;
    }

    /**
     * Update conveyor physics - call this each physics step.
     * 
     * Uses SPATIAL DETECTION: checks all packages and applies belt
     * physics to any that are within the detection zone.
     * 
     * Rotation stabilization:
     * - Locks rotations while on belt (prevents physics-induced rotation)
     * - Unlocks when leaving belt for natural tumbling
     * 
     * @param _dt Delta time in seconds (unused, kept for API consistency)
     * @returns Array of body handles that fell off the belt
     */
    update(_dt: number): number[] {
        const beltSpeed = this.getBeltSpeed();
        const fellOff: number[] = [];

        // Update animation offset for rendering
        this.animationOffset += beltSpeed * _dt;

        // Get all package bodies
        const allBodies = this.physics.getAllBodies();

        for (const body of allBodies) {
            // Skip held bodies and clean up their stabilization
            if (body.userData.isHeld) {
                this.handleBeltExit(body.handle);
                body.userData.isOnConveyor = false;
                continue;
            }

            const wasOnConveyor = body.userData.isOnConveyor === true;
            
            // Check if body is on the belt
            if (!this.isBodyOnBelt(body)) {
                // If it was on conveyor but is now past the end, it fell off
                if (wasOnConveyor) {
                    const pos = this.physics.getPosition(body.handle);
                    if (pos && pos.x > this.geometry.endX) {
                        fellOff.push(body.handle);
                    }
                    // Clean up stabilization when leaving belt
                    this.handleBeltExit(body.handle);
                }
                body.userData.isOnConveyor = false;
                continue;
            }

            // Body is on the belt - apply belt physics
            body.userData.isOnConveyor = true;

            // Apply belt velocity (override X, preserve Y for settling)
            const currentVel = this.physics.getVelocity(body.handle);
            if (currentVel) {
                this.physics.setVelocity(body.handle, beltSpeed, currentVel.y);
            }

            // Handle belt entry: lock rotation
            if (!this.stabilization.has(body.handle)) {
                this.handleBeltEntry(body.handle);
            }
            
            // No need to zero angular velocity - lockRotations handles it
        }

        return fellOff;
    }

    /**
     * Handle a package entering the belt.
     * Locks rotation and applies high angular damping.
     */
    private handleBeltEntry(handle: number): void {
        const currentAngle = this.physics.getRotation(handle);
        
        if (currentAngle === null) return;
        
        // Store stabilization state
        this.stabilization.set(handle, {
            targetAngle: currentAngle,
        });
        
        // Lock rotations - prevents any physics-induced rotation
        this.physics.lockRotations(handle, true);
        
        // Also zero any existing angular velocity
        this.physics.setAngularVelocity(handle, 0);
    }

    /**
     * Handle a package leaving the belt.
     * Unlocks rotation and restores default angular damping.
     */
    private handleBeltExit(handle: number): void {
        const state = this.stabilization.get(handle);
        if (!state) return;
        
        // Unlock rotations - allow physics to rotate the body again
        this.physics.lockRotations(handle, false);
        
        // Restore default angular damping
        this.physics.setAngularDamping(handle, CONVEYOR_PHYSICS.defaultAngularDamping);
        
        // Zero angular velocity to prevent spin from belt
        this.physics.setAngularVelocity(handle, 0);
        
        // Clean up
        this.stabilization.delete(handle);
    }

    /**
     * Get count of packages currently on the belt.
     */
    getPackageCount(): number {
        let count = 0;
        for (const body of this.physics.getAllBodies()) {
            if (body.userData.isOnConveyor) {
                count++;
            }
        }
        return count;
    }

    /**
     * Remove all packages that are on the conveyor from the world.
     */
    clearPackages(): void {
        const toRemove: number[] = [];
        for (const body of this.physics.getAllBodies()) {
            if (body.userData.isOnConveyor) {
                toRemove.push(body.handle);
            }
        }
        for (const handle of toRemove) {
            this.physics.removeBody(handle);
            this.stabilization.delete(handle);
        }
    }

    /**
     * Clean up resources.
     */
    destroy(): void {
        if (this.beltBodyHandle !== null) {
            this.physics.removeStaticBody(this.beltBodyHandle);
            this.beltBodyHandle = null;
        }
        this.stabilization.clear();
    }
}
